import { Link } from "react-router-dom";
import { FastViLogo } from "../components/FastViLogo";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="px-6 md:px-10 py-5 flex items-center justify-between border-b border-border">
        <Link to="/" className="flex items-center gap-3" data-testid="privacy-home">
          <FastViLogo size={44}/>
          <span className="font-display text-xl">Fast<span className="text-accent">Vi</span></span>
        </Link>
        <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="privacy-login">Sign in</Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12" data-testid="privacy-page">
        <div className="label-tiny mb-3">Policy</div>
        <h1 className="font-display text-4xl md:text-5xl mb-4">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-10">
          Effective <span className="font-mono">{new Date().toLocaleDateString()}</span>.
          This policy explains what FastVi collects, how it's used, and the rights you have as a resident, guard, admin or visitor.
        </p>

        <section className="space-y-10 text-sm leading-7">
          <div>
            <h2 className="font-display text-2xl mb-3">1. Who we are</h2>
            <p>
              FastVi is a secure video intercom and visitor-pass platform operated for the residential building whose code you were invited with.
              The building administrator is the <span className="font-medium">data controller</span>; FastVi acts as the <span className="font-medium">processor</span> on their behalf.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3">2. What we collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account data:</strong> name, email, hashed password, role, phone (optional), building and unit assignment.</li>
              <li><strong>Call metadata:</strong> timestamps, caller unit, visitor name entered at the kiosk, call duration, answer/decline/no-answer status.</li>
              <li><strong>Visitor photo at kiosk:</strong> a single still image captured when a visitor rings a unit, stored only until the call ends or the backend retention window expires.</li>
              <li><strong>WebRTC media:</strong> real-time audio/video streams flow peer-to-peer (or via a TURN relay only when direct connection fails). Media is <strong>not recorded</strong> or stored by FastVi.</li>
              <li><strong>Visitor passes:</strong> code, visitor name, purpose, validity window, issuing resident.</li>
              <li><strong>Door events:</strong> who unlocked a door, from which unit, and the result of the hardware pulse delivery.</li>
              <li><strong>Push notification subscriptions:</strong> the opaque browser endpoint and public keys needed to deliver ring-notifications to your device.</li>
              <li><strong>Operational logs:</strong> login events, forced logouts, pass revocations and other admin actions — kept for audit.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3">3. How we use it</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Delivering the core service: ringing residents, admitting visitors, unlocking doors.</li>
              <li>Securing accounts and preventing abuse (rate-limiting, session invalidation).</li>
              <li>Giving building admins and guards the audit trail they need to run the building safely.</li>
              <li>Debugging platform issues at an aggregate level.</li>
            </ul>
            <p className="mt-3">We do not sell your data. We do not use it for advertising. We do not train external AI models on it.</p>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3">4. Retention</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Call & activity log: retained for the operational lifetime of the building deployment (admin can purge).</li>
              <li>Visitor photos: short-lived, tied to the call document and purged when the building is deleted.</li>
              <li>Push subscriptions: removed when you sign out of that browser or revoke notifications.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3">5. Who can see what</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Residents</strong> see their own calls, passes and door events — nothing from other units.</li>
              <li><strong>Guards</strong> see live calls and passes for their assigned buildings.</li>
              <li><strong>Building admins</strong> see everything inside their building.</li>
              <li><strong>Super admins</strong> see cross-building aggregates and can perform operational actions, logged in the audit trail.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3">6. Security</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Passwords are hashed with bcrypt. We never store plain-text credentials.</li>
              <li>Sessions use short-lived HTTP-only cookies and can be revoked instantly via Force Logout.</li>
              <li>Door-unlock requests to on-prem hardware are signed with per-building HMAC secrets.</li>
              <li>Communication is TLS-encrypted end-to-end between your browser and the FastVi backend.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3">7. Your rights</h2>
            <p>
              You can request a copy of your account data, request correction of inaccuracies, or ask your building admin
              to delete your account. For the super admin bootstrap account, contact your platform operator directly.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3">8. Contact</h2>
            <p>
              Questions about this policy go to your building administrator in the first instance. For platform-level
              concerns reach the FastVi operator for your deployment.
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-border px-6 md:px-10 py-8 text-sm text-muted-foreground flex flex-col md:flex-row justify-between items-center gap-3">
        <div>© {new Date().getFullYear()} FastVi · Secure intercom for modern buildings</div>
        <div className="flex gap-4 font-mono text-xs">
          <Link to="/" data-testid="privacy-footer-home">/home</Link>
          <Link to="/login" data-testid="privacy-footer-login">/login</Link>
          <Link to="/privacy" data-testid="privacy-footer-privacy">/privacy</Link>
        </div>
      </footer>
    </div>
  );
}
